import { ElementType, LineType } from '@ritabygglov/core';
import * as KeyCode from 'keycode-js';
import {
  SelectionMode,
  SelectionModeHelper,
  SelectionModes,
} from '../../types';

/**
 * A declarative description of what a key press means, independent of any state
 * mutation or side effect. Produced by {@link mapKeyToAction} and consumed by
 * {@link handleKeyboardAction}.
 */
export type KeyboardAction =
  | { type: 'Undo' }
  | { type: 'Redo' }
  | { type: 'ToggleSnaplines' }
  | { type: 'GroupSelection' }
  | { type: 'UngroupSelection' }
  | { type: 'ExitGroupEditing' }
  | { type: 'ExitTextEditing' }
  | { type: 'ConfirmEnter' }
  | { type: 'CancelSelection' }
  | { type: 'PressSpace' }
  | { type: 'PressShift' }
  | { type: 'PressMeta' }
  | { type: 'ReleaseSpace' }
  | { type: 'ReleaseShift' }
  | { type: 'ReleaseMeta' }
  | { type: 'DeleteSelected' }
  | { type: 'SetAddMode'; elementType: ElementType; lineType?: LineType }
  | { type: 'Duplicate' }
  | { type: 'Copy' }
  | { type: 'Nudge'; code: string };

export interface KeyboardMapContext {
  isMetaPressed: boolean;
  isShiftPressed: boolean;
  selectionMode: SelectionMode;
}

const isArrowKey = (code: string): boolean =>
  code === KeyCode.CODE_UP ||
  code === KeyCode.CODE_DOWN ||
  code === KeyCode.CODE_LEFT ||
  code === KeyCode.CODE_RIGHT;

const isSpaceCode = (code: string): boolean => code === KeyCode.CODE_SPACE;

const isShiftCode = (code: string): boolean =>
  code === KeyCode.CODE_SHIFT_LEFT || code === KeyCode.CODE_SHIFT_RIGHT;

// The "meta" modifier is Cmd on macOS and Ctrl on Windows/Linux.
const isMetaCode = (code: string): boolean =>
  code === KeyCode.CODE_META_LEFT ||
  code === KeyCode.CODE_META_RIGHT ||
  code === KeyCode.CODE_CONTROL_LEFT ||
  code === KeyCode.CODE_CONTROL_RIGHT;

const isFocusedInPropertyOption = (target: EventTarget | null): boolean =>
  target instanceof HTMLInputElement && target.id === 'property-option';

/**
 * Pure mapping from a keyboard event (plus the currently tracked modifiers and
 * selection mode) to a single {@link KeyboardAction}, or `null` when the key is
 * not bound.
 *
 * This function performs no side effects and does not read component state
 * beyond what is passed in `ctx`, which makes the full keyboard binding table
 * unit-testable in isolation.
 */
export const mapKeyToAction = (
  e: KeyboardEvent,
  ctx: KeyboardMapContext
): KeyboardAction | null => {
  const { isMetaPressed, isShiftPressed, selectionMode } = ctx;

  // Undo / redo and the snapline toggle are handled before any input guard or
  // the text-editing branch, so they remain available globally.
  if (e.code === KeyCode.CODE_Z && isMetaPressed && isShiftPressed) {
    return { type: 'Redo' };
  }
  if (e.code === KeyCode.CODE_Z && isMetaPressed) {
    return { type: 'Undo' };
  }

  if (e.code === KeyCode.CODE_O && isMetaPressed && isShiftPressed) {
    return { type: 'ToggleSnaplines' };
  }

  // Ignore every other binding while typing in a property option input.
  if (isFocusedInPropertyOption(e.target)) {
    return null;
  }

  if (selectionMode.type === SelectionModes.TextEditing) {
    if (e.code === KeyCode.CODE_ESCAPE) {
      return { type: 'ExitTextEditing' };
    }
    return null;
  }

  if (e.code === KeyCode.CODE_G && isMetaPressed && isShiftPressed) {
    return { type: 'UngroupSelection' };
  }
  if (e.code === KeyCode.CODE_G && isMetaPressed) {
    return { type: 'GroupSelection' };
  }

  // Escape closes an open group before it reaches the generic cancel below.
  if (
    e.code === KeyCode.CODE_ESCAPE &&
    SelectionModeHelper.isGroupEditing(selectionMode)
  ) {
    return { type: 'ExitGroupEditing' };
  }

  if (e.code === KeyCode.CODE_ENTER) {
    return { type: 'ConfirmEnter' };
  }
  if (
    e.code === KeyCode.CODE_ESCAPE ||
    (e.code === KeyCode.CODE_V && !isMetaPressed)
  ) {
    return { type: 'CancelSelection' };
  }
  if (isSpaceCode(e.code)) {
    return { type: 'PressSpace' };
  }
  if (isShiftCode(e.code)) {
    return { type: 'PressShift' };
  }
  if (e.code === KeyCode.CODE_BACK_SPACE || e.code === KeyCode.CODE_DELETE) {
    return { type: 'DeleteSelected' };
  }
  if (isMetaCode(e.code)) {
    return { type: 'PressMeta' };
  }
  if (e.code === KeyCode.CODE_R) {
    return { type: 'SetAddMode', elementType: ElementType.Rect };
  }
  if (e.code === KeyCode.CODE_T) {
    return { type: 'SetAddMode', elementType: ElementType.Text };
  }
  if (e.code === KeyCode.CODE_L) {
    return { type: 'SetAddMode', elementType: ElementType.Polyline };
  }
  if (e.code === KeyCode.CODE_M) {
    return {
      type: 'SetAddMode',
      elementType: ElementType.Polyline,
      lineType: LineType.Dimension,
    };
  }
  if (isMetaPressed && e.code === KeyCode.CODE_D) {
    return { type: 'Duplicate' };
  }
  if (isMetaPressed && e.code === KeyCode.CODE_C) {
    return { type: 'Copy' };
  }
  if (isArrowKey(e.code)) {
    return { type: 'Nudge', code: e.code };
  }

  return null;
};

/**
 * Pure mapping from a key-up event to a modifier-release action, or `null` when
 * the released key is not a tracked modifier. Mirrors the `PressSpace` /
 * `PressShift` / `PressMeta` actions produced by {@link mapKeyToAction}.
 */
export const mapKeyUpToAction = (e: KeyboardEvent): KeyboardAction | null => {
  if (isSpaceCode(e.code)) {
    return { type: 'ReleaseSpace' };
  }
  if (isMetaCode(e.code)) {
    return { type: 'ReleaseMeta' };
  }
  if (isShiftCode(e.code)) {
    return { type: 'ReleaseShift' };
  }
  return null;
};
